import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChartIcon, TrendingUpIcon, ShoppingCartIcon, UsersIcon, PackageIcon, ArrowUpIcon, ArrowDownIcon, DollarSignIcon, CalendarIcon, InfoIcon } from 'lucide-react';
import { Shop } from '../pages/Shops';
interface ShopDashboardProps {
  shop: Shop;
}
export function ShopDashboard({
  shop
}: ShopDashboardProps) {
  const {
    t
  } = useTranslation('shops');
  const [hoveredRevenue, setHoveredRevenue] = useState<{
    month: string;
    revenue: number;
  } | null>(null);
  const [hoveredOrder, setHoveredOrder] = useState<{
    month: string;
    orders: number;
  } | null>(null);
  const seasonalPattern = [0.25, 0.3, 0.45, 0.6, 0.75, 0.9, 1.0, 0.95, 0.7, 0.5, 0.35, 0.4];
  // Generate revenue data with more pronounced seasonal patterns
  const revenueData = [{
    month: 'Jan',
    revenue: Math.round(shop.revenue.total * seasonalPattern[0])
  }, {
    month: 'Feb',
    revenue: Math.round(shop.revenue.total * seasonalPattern[1])
  }, {
    month: 'Mar',
    revenue: Math.round(shop.revenue.total * seasonalPattern[2])
  }, {
    month: 'Apr',
    revenue: Math.round(shop.revenue.total * seasonalPattern[3])
  }, {
    month: 'May',
    revenue: Math.round(shop.revenue.total * seasonalPattern[4])
  }, {
    month: 'Jun',
    revenue: Math.round(shop.revenue.total * seasonalPattern[5])
  }, {
    month: 'Jul',
    revenue: Math.round(shop.revenue.total * seasonalPattern[6])
  }, {
    month: 'Aug',
    revenue: Math.round(shop.revenue.total * seasonalPattern[7])
  }, {
    month: 'Sep',
    revenue: Math.round(shop.revenue.total * seasonalPattern[8])
  }, {
    month: 'Oct',
    revenue: Math.round(shop.revenue.total * seasonalPattern[9])
  }, {
    month: 'Nov',
    revenue: Math.round(shop.revenue.total * seasonalPattern[10])
  }, {
    month: 'Dec',
    revenue: Math.round(shop.revenue.total * seasonalPattern[11])
  }];
  // Generate orders data with the same seasonal pattern
  const ordersData = [{
    month: 'Jan',
    orders: Math.round(shop.ordersCount * seasonalPattern[0])
  }, {
    month: 'Feb',
    orders: Math.round(shop.ordersCount * seasonalPattern[1])
  }, {
    month: 'Mar',
    orders: Math.round(shop.ordersCount * seasonalPattern[2])
  }, {
    month: 'Apr',
    orders: Math.round(shop.ordersCount * seasonalPattern[3])
  }, {
    month: 'May',
    orders: Math.round(shop.ordersCount * seasonalPattern[4])
  }, {
    month: 'Jun',
    orders: Math.round(shop.ordersCount * seasonalPattern[5])
  }, {
    month: 'Jul',
    orders: Math.round(shop.ordersCount * seasonalPattern[6])
  }, {
    month: 'Aug',
    orders: Math.round(shop.ordersCount * seasonalPattern[7])
  }, {
    month: 'Sep',
    orders: Math.round(shop.ordersCount * seasonalPattern[8])
  }, {
    month: 'Oct',
    orders: Math.round(shop.ordersCount * seasonalPattern[9])
  }, {
    month: 'Nov',
    orders: Math.round(shop.ordersCount * seasonalPattern[10])
  }, {
    month: 'Dec',
    orders: Math.round(shop.ordersCount * seasonalPattern[11])
  }];
  const topProducts = [{
    name: 'Beach Towel',
    sales: 38,
    revenue: 1139.62
  }, {
    name: 'Sunglasses',
    sales: 27,
    revenue: 1079.73
  }, {
    name: 'Swim Shorts',
    sales: 24,
    revenue: 839.76
  }, {
    name: 'Beach Sandals',
    sales: 22,
    revenue: 549.78
  }];
  const customerStats = {
    total: 287,
    new: 42,
    returning: 245,
    conversionRate: 3.8
  };
  const maxRevenue = Math.max(...revenueData.map(item => item.revenue));
  const maxOrders = Math.max(...ordersData.map(item => item.orders));
  return <div className="space-y-6" data-id="element-10494">
      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-10495">
        {/* Revenue Chart */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-10496">
          <div className="flex justify-between items-center mb-4" data-id="element-10497">
            <h4 className="text-sm font-semibold text-gray-900" data-id="element-10498">
              {t('components.ShopDashboard.revenue_trend')}
            </h4>
            <div className="flex items-center text-xs text-gray-500" data-id="element-10499">
              <CalendarIcon size={14} className="mr-1" data-id="element-10500" />
              {t('components.ShopDashboard.last_12_months')}
            </div>
          </div>
          <div className="relative h-60 mb-6" data-id="element-10501">
            {hoveredRevenue && <div className="absolute top-0 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded-lg py-1.5 px-3 z-10 shadow-lg" data-id="element-10502">
                {hoveredRevenue.month}
                {t('components.ShopDashboard.usd')}{' '}
                {hoveredRevenue.revenue.toLocaleString('en-US')}
              </div>}
            <div className="absolute top-0 left-0 h-full flex flex-col justify-between text-xs text-gray-400 pr-2" data-id="element-10503">
              <div data-id="element-10504">
                {t('components.ShopDashboard.usd_2')}{' '}
                {maxRevenue.toLocaleString('en-US')}
              </div>
              <div data-id="element-10505">
                {t('components.ShopDashboard.usd_2')}{' '}
                {Math.round(maxRevenue * 0.75).toLocaleString('en-US')}
              </div>
              <div data-id="element-10506">
                {t('components.ShopDashboard.usd_2')}{' '}
                {Math.round(maxRevenue * 0.5).toLocaleString('en-US')}
              </div>
              <div data-id="element-10507">
                {t('components.ShopDashboard.usd_2')}{' '}
                {Math.round(maxRevenue * 0.25).toLocaleString('en-US')}
              </div>
              <div data-id="element-10508">{t('components.ShopDashboard.usd_0')}</div>
            </div>
            <div className="absolute left-12 right-0 h-full flex items-end" data-id="element-10509">
              <div className="absolute inset-0" data-id="element-10510">
                <div className="border-t border-gray-100 h-1/4" data-id="element-10511"></div>
                <div className="border-t border-gray-100 h-1/4" data-id="element-10512"></div>
                <div className="border-t border-gray-100 h-1/4" data-id="element-10513"></div>
                <div className="border-t border-gray-100 h-1/4" data-id="element-10514"></div>
              </div>
              <div className="relative w-full h-full flex items-end" data-id="element-10515">
                {revenueData.map((item, index) => {
                const barHeight = `${item.revenue / maxRevenue * 100}%`;
                return <div key={index} className="flex-1 flex flex-col items-center justify-end h-full" onMouseEnter={() => setHoveredRevenue(item)} onMouseLeave={() => setHoveredRevenue(null)} data-id="element-10516">
                      <div style={{
                    height: barHeight
                  }} className={`w-4/5 ${index === 11 ? 'bg-[#FFC70A]' : 'bg-[#FFC70A]/70 hover:bg-[#FFC70A]'} rounded-t transition-all duration-200`} data-id="element-10517"></div>
                    </div>;
              })}
              </div>
            </div>
            <div className="absolute bottom-0 left-12 right-0 flex justify-between text-xs text-gray-400 pt-2" data-id="element-10518">
              {revenueData.map((item, index) => <div key={index} className="text-center" data-id="element-10519">
                  {item.month}
                </div>)}
            </div>
          </div>
        </div>

        {/* Orders Chart */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-10520">
          <div className="flex justify-between items-center mb-4" data-id="element-10521">
            <h4 className="text-sm font-semibold text-gray-900" data-id="element-10522">
              {t('components.ShopDashboard.orders_trend')}
            </h4>
            <div className="flex items-center text-xs text-gray-500" data-id="element-10523">
              <CalendarIcon size={14} className="mr-1" data-id="element-10524" />
              {t('components.ShopDashboard.last_12_months')}
            </div>
          </div>
          <div className="relative h-60 mb-6" data-id="element-10525">
            {hoveredOrder && <div className="absolute top-0 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded-lg py-1.5 px-3 z-10 shadow-lg" data-id="element-10526">
                {hoveredOrder.month}: {hoveredOrder.orders.toLocaleString()}{' '}
                {t('components.ShopDashboard.orders')}
              </div>}
            <div className="absolute top-0 left-0 h-full flex flex-col justify-between text-xs text-gray-400 pr-2" data-id="element-10527">
              <div data-id="element-10528">{maxOrders.toLocaleString()}</div>
              <div data-id="element-10529">{Math.round(maxOrders * 0.75).toLocaleString()}</div>
              <div data-id="element-10530">{Math.round(maxOrders * 0.5).toLocaleString()}</div>
              <div data-id="element-10531">{Math.round(maxOrders * 0.25).toLocaleString()}</div>
              <div data-id="element-10532">0</div>
            </div>
            <div className="absolute left-12 right-0 h-full flex items-end" data-id="element-10533">
              <div className="absolute inset-0" data-id="element-10534">
                <div className="border-t border-gray-100 h-1/4" data-id="element-10535"></div>
                <div className="border-t border-gray-100 h-1/4" data-id="element-10536"></div>
                <div className="border-t border-gray-100 h-1/4" data-id="element-10537"></div>
                <div className="border-t border-gray-100 h-1/4" data-id="element-10538"></div>
              </div>
              <div className="relative w-full h-full flex items-end" data-id="element-10539">
                {ordersData.map((item, index) => {
                const barHeight = `${item.orders / maxOrders * 100}%`;
                return <div key={index} className="flex-1 flex flex-col items-center justify-end h-full" onMouseEnter={() => setHoveredOrder(item)} onMouseLeave={() => setHoveredOrder(null)} data-id="element-10540">
                      <div style={{
                    height: barHeight
                  }} className={`w-4/5 ${index === 11 ? 'bg-purple-600' : 'bg-purple-400 hover:bg-purple-600'} rounded-t transition-all duration-200`} data-id="element-10541"></div>
                    </div>;
              })}
              </div>
            </div>
            <div className="absolute bottom-0 left-12 right-0 flex justify-between text-xs text-gray-400 pt-2" data-id="element-10542">
              {ordersData.map((item, index) => <div key={index} className="text-center" data-id="element-10543">
                  {item.month}
                </div>)}
            </div>
          </div>
        </div>
      </div>

      {/* Additional Insights Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-10544">
        {/* Top Products */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-10545">
          <h4 className="text-sm font-semibold text-gray-900 mb-4" data-id="element-10546">
            {t('components.ShopDashboard.top_selling_products')}
          </h4>
          <div className="space-y-3" data-id="element-10547">
            {topProducts.map((product, index) => <div key={index} className="flex items-center justify-between border-b border-gray-100 pb-3 last:border-0 last:pb-0" data-id="element-10548">
                <div className="flex items-center" data-id="element-10549">
                  <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center text-xs font-semibold text-gray-500 mr-3" data-id="element-10550">
                    {index + 1}
                  </div>
                  <div data-id="element-10551">
                    <p className="text-sm font-medium text-gray-900" data-id="element-10552">
                      {product.name}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-10553">
                      {product.sales} {t('components.ShopDashboard.sold')}
                    </p>
                  </div>
                </div>
                <div className="text-right" data-id="element-10554">
                  <p className="text-sm font-medium text-gray-900" data-id="element-10555">
                    {t('components.ShopDashboard.usd_2')}{' '}
                    {product.revenue.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-10556">
                    {t('components.ShopDashboard.revenue')}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        {/* Customer Insights */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-10557">
          <h4 className="text-sm font-semibold text-gray-900 mb-4" data-id="element-10558">
            {t('components.ShopDashboard.customer_insights')}
          </h4>
          <div className="grid grid-cols-2 gap-4" data-id="element-10559">
            <div className="bg-gray-50 rounded-lg p-4" data-id="element-10560">
              <p className="text-xs text-gray-500 mb-1" data-id="element-10561">
                {t('components.ShopDashboard.new_customers')}
              </p>
              <p className="text-lg font-bold text-gray-900" data-id="element-10562">
                {customerStats.new}
              </p>
              <div className="mt-1 flex items-center" data-id="element-10563">
                <span className="text-xs text-green-600 flex items-center" data-id="element-10564">
                  <ArrowUpIcon size={10} className="mr-1" data-id="element-10565" />
                  14.3%
                </span>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4" data-id="element-10566">
              <p className="text-xs text-gray-500 mb-1" data-id="element-10567">
                {t('components.ShopDashboard.returning_customers')}
              </p>
              <p className="text-lg font-bold text-gray-900" data-id="element-10568">
                {customerStats.returning}
              </p>
              <div className="mt-1 flex items-center" data-id="element-10569">
                <span className="text-xs text-green-600 flex items-center" data-id="element-10570">
                  <ArrowUpIcon size={10} className="mr-1" data-id="element-10571" />
                  7.2%
                </span>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4" data-id="element-10572">
              <p className="text-xs text-gray-500 mb-1" data-id="element-10573">
                {t('components.ShopDashboard.avg_order_value')}
              </p>
              <p className="text-lg font-bold text-gray-900" data-id="element-10574">
                {t('components.ShopDashboard.usd_2')}{' '}
                {(shop.revenue.total / shop.ordersCount).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
              </p>
              <div className="mt-1 flex items-center" data-id="element-10575">
                <span className="text-xs text-green-600 flex items-center" data-id="element-10576">
                  <ArrowUpIcon size={10} className="mr-1" data-id="element-10577" />
                  3.5%
                </span>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4" data-id="element-10578">
              <p className="text-xs text-gray-500 mb-1" data-id="element-10579">
                {t('components.ShopDashboard.conversion_rate')}
              </p>
              <p className="text-lg font-bold text-gray-900" data-id="element-10580">
                {customerStats.conversionRate}%
              </p>
              <div className="mt-1 flex items-center" data-id="element-10581">
                <span className="text-xs text-green-600 flex items-center" data-id="element-10582">
                  <ArrowUpIcon size={10} className="mr-1" data-id="element-10583" />
                  1.2%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}